import { getProfile, putProfile, type ProfilePin } from './registry';

/**
 * The profile PIN (docs/TZ-PROFILES.md 4.5): digits only, 4–8. Only a salt and a PBKDF2-SHA-256 hash
 * are kept. It closes the interface, it does not encrypt the data. After 5 wrong tries in a row each
 * next try waits (30 s, 1, 5, 15 minutes); the count lives in the registry, so a reload does not reset it.
 */
export const PIN_RE = /^\d{4,8}$/;
export const PIN_ITERATIONS = 210_000;
export const FREE_TRIES = 5;

const b64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations }, key, 256);
  return new Uint8Array(bits);
}

export async function hashPin(pin: string, iterations = PIN_ITERATIONS): Promise<ProfilePin> {
  if (!PIN_RE.test(pin)) throw new Error('a PIN is 4 to 8 digits');
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return { salt: b64(salt), hash: b64(await derive(pin, salt, iterations)), iterations };
}

export async function checkPin(pin: string, p: ProfilePin): Promise<boolean> {
  if (!PIN_RE.test(pin)) return false;
  const got = await derive(pin, unb64(p.salt), p.iterations);
  const want = unb64(p.hash);
  if (got.length !== want.length) return false;
  let diff = 0;
  for (let i = 0; i < got.length; i++) diff |= got[i] ^ want[i];
  return diff === 0;
}

/** How long the next try waits after so many wrong ones in a row. */
export function pinDelay(failed: number): number {
  if (failed < FREE_TRIES) return 0;
  const steps = [30_000, 60_000, 300_000, 900_000];
  return steps[Math.min(failed - FREE_TRIES, steps.length - 1)];
}

export type PinResult = { ok: true } | { ok: false; wait: number; triesLeft: number };

/** Try a PIN for a profile: counts wrong tries, makes the next one wait after five. */
export async function tryPin(id: string, pin: string, now = Date.now()): Promise<PinResult> {
  const p = await getProfile(id);
  if (!p?.pin) return { ok: true };
  if (p.lockedUntil > now) return { ok: false, wait: p.lockedUntil - now, triesLeft: 0 };
  if (await checkPin(pin, p.pin)) {
    if (p.failed || p.lockedUntil) await putProfile({ ...p, failed: 0, lockedUntil: 0 });
    return { ok: true };
  }
  const failed = p.failed + 1;
  const wait = pinDelay(failed);
  await putProfile({ ...p, failed, lockedUntil: wait ? now + wait : 0 });
  return { ok: false, wait, triesLeft: Math.max(0, FREE_TRIES - failed) };
}
